import { motion } from "framer-motion";
import {
  Mail,
  Shield,
  Activity,
  Grid3x3,
  Code2,
  Workflow,
  Cloud,
  AlertOctagon,
  ArrowUpRight,
} from "lucide-react";

/* Mock SOC data — purely visual, no PII */
const KQL_SAMPLE = [
  ["SigninLogs"],
  ["| where ", "TimeGenerated > ago(1d)"],
  ["| where ", "ResultType == 0"],
  ["| where ", 'RiskLevelDuringSignIn == "high"'],
  ["| extend ", 'country = tostring(LocationDetails.countryOrRegion)'],
  ["| summarize ", 'ips=make_set(IPAddress), countries=make_set(country) by UserPrincipalName'],
  ["| where ", "array_length(countries) > 1"],
  ["| project ", "UserPrincipalName, ips, countries"],
];

const ALERTS = [
  { sev: "CRITICAL", text: "Suspicious OAuth consent grant", time: "2m" },
  { sev: "HIGH", text: "Impossible travel — IN ↔ NL", time: "8m" },
  { sev: "MEDIUM", text: "Anomalous PowerShell execution", time: "23m" },
  { sev: "HIGH", text: "Mass file download — OneDrive", time: "41m" },
  { sev: "LOW", text: "Sign-in from new device", time: "1h" },
];

const sevColor = {
  CRITICAL: { dot: "#F87171", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.30)" },
  HIGH:     { dot: "#FB923C", bg: "rgba(251,146,60,0.10)", border: "rgba(251,146,60,0.30)" },
  MEDIUM:   { dot: "#FACC15", bg: "rgba(250,204,21,0.08)", border: "rgba(250,204,21,0.30)" },
  LOW:      { dot: "#60A5FA", bg: "rgba(96,165,250,0.08)", border: "rgba(96,165,250,0.25)" },
};

const TACTICS = [
  ["Recon", 0.4], ["Init.Access", 0.9], ["Execution", 0.6], ["Persistence", 0.5],
  ["Priv.Esc", 0.7], ["Def.Evasion", 0.85], ["Cred.Access", 0.6], ["Discovery", 0.45],
  ["Lateral", 0.55], ["Collection", 0.35], ["Exfil", 0.7], ["Impact", 0.5],
];

const LABS = [
  { code: "LAB-01", icon: Mail,         title: "Phishing Simulations",         ttp: "T1566",     accent: "blue",
    desc: "Adversary-grade campaigns with telemetry rolled back into your SOC for click-through, credential-harvest and lateral-movement analysis." },
  { code: "LAB-02", icon: Shield,       title: "Defender XDR Investigations",  ttp: "DETECT",    accent: "cyan",
    desc: "End-to-end live investigations across endpoint, identity, cloud apps and Office workloads using the unified Defender portal." },
  { code: "LAB-03", icon: Activity,     title: "Sentinel Analytics",           ttp: "DETECT",    accent: "indigo",
    desc: "Author analytics rules, workbooks and incident graphs on real telemetry — from custom KQL to ML-based anomaly detections." },
  { code: "LAB-04", icon: Grid3x3,      title: "MITRE ATT&CK Mapping",         ttp: "FRAMEWORK", accent: "blue",
    desc: "Map detection and control coverage to the ATT&CK matrix — surface defensive gaps tactic-by-tactic with confidence scoring." },
  { code: "LAB-05", icon: Code2,        title: "KQL Threat Hunting",           ttp: "HUNT",      accent: "cyan",
    desc: "Hands-on KQL hunts across signin, audit, device, OAuth and cloud-app sources — adversary TTPs translated into shippable queries." },
  { code: "LAB-06", icon: Workflow,     title: "SOAR Workflows",               ttp: "RESPOND",   accent: "indigo",
    desc: "Auto-containment, enrichment and case-orchestration via Logic Apps — cut MTTR by automating the analyst's top-15 manual actions." },
  { code: "LAB-07", icon: Cloud,        title: "Cloud Attack Simulations",     ttp: "T1078.004", accent: "blue",
    desc: "Live tenant-level attack simulations across Azure & Entra — token theft, OAuth abuse, Conditional-Access bypass under controlled conditions." },
  { code: "LAB-08", icon: AlertOctagon, title: "Incident Response Exercises",  ttp: "IR",        accent: "cyan",
    desc: "Tabletop and live-fire IR drills covering containment, eradication, recovery, exec-comms and post-incident lessons-learned." },
];

const accentMap = {
  blue:   { dot: "#60A5FA", border: "rgba(96,165,250,0.30)",  soft: "rgba(96,165,250,0.10)",  glow: "rgba(37,99,235,0.18)" },
  cyan:   { dot: "#22D3EE", border: "rgba(34,211,238,0.30)",  soft: "rgba(34,211,238,0.10)",  glow: "rgba(6,182,212,0.18)" },
  indigo: { dot: "#818CF8", border: "rgba(129,140,248,0.30)", soft: "rgba(129,140,248,0.10)", glow: "rgba(99,102,241,0.18)" },
};

export default function Labs() {
  return (
    <section
      id="labs"
      data-testid="labs-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          background: "radial-gradient(800px 420px at 95% 0%, rgba(37,99,235,0.12), transparent 60%), radial-gradient(700px 380px at 0% 100%, rgba(6,182,212,0.08), transparent 65%)",
        }} />
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(rgba(96,165,250,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-blue-500" />
          <span className="label-eyebrow">04 — Labs &amp; Attack Simulations</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-12 md:mb-14 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.04] text-white">
            Where adversaries fail. <br />
            <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(90deg,#67E8F9 0%,#60A5FA 50%,#A5B4FC 100%)",
            }}>Hands-on, in your tenant.</span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed text-base md:text-lg">
            Live attack simulations, detection engineering and SOC drills run on
            real telemetry. No slideware. No theoretical labs. Production-grade
            from minute one.
          </p>
        </div>

        {/* SOC DASHBOARD MOCK */}
        <div
          data-testid="soc-dashboard-mock"
          className="rounded-2xl overflow-hidden mb-12 md:mb-14"
          style={{
            background: "linear-gradient(180deg,#070A12 0%,#050810 100%)",
            border: "1px solid rgba(96,165,250,0.25)",
            boxShadow: "0 30px 80px -30px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          {/* Top status bar */}
          <div className="flex items-center justify-between px-5 md:px-7 py-3 border-b border-blue-400/15 bg-black/40">
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.22em]">
              <span className="inline-flex items-center gap-2 text-emerald-300">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                </span>
                LIVE · Sentinel
              </span>
              <span className="text-slate-500 hidden sm:inline">workspace: <span className="text-blue-300">aksahni-soc-lab</span></span>
              <span className="text-slate-500 hidden md:inline">tier: <span className="text-blue-300">T2 hunt</span></span>
            </div>
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
              <span className="text-amber-300">THREAT_LVL: ELEVATED</span>
              <div className="hidden sm:flex items-center gap-1.5">
                {[...Array(6)].map((_, i) => (
                  <span key={i} className="h-1.5 w-1.5 rounded-full" style={{
                    background: i < 4 ? "#FB923C" : "rgba(255,255,255,0.15)",
                  }} />
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* KQL editor */}
            <div className="lg:col-span-7 border-b lg:border-b-0 lg:border-r border-blue-400/10">
              <div className="px-5 md:px-7 py-3 border-b border-blue-400/10 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                  query · <span className="text-blue-300">impossible_travel.kql</span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">200 OK · 12 rows</div>
              </div>
              <pre className="px-5 md:px-7 py-5 overflow-x-auto text-[12px] md:text-[13px] leading-[1.65]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {KQL_SAMPLE.map((line, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-slate-700 select-none w-5 text-right">{i + 1}</span>
                    <span>
                      {line.length === 1 ? (
                        <span style={{ color: "#60A5FA" }}>{line[0]}</span>
                      ) : (
                        <>
                          <span style={{ color: "#22D3EE" }}>{line[0]}</span>
                          <span className="text-slate-200">{line[1]}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
                <div className="mt-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-sm bg-blue-400 animate-pulse" />
                  <span className="text-slate-500 text-[11px]">cursor</span>
                </div>
              </pre>
            </div>

            {/* Right side: alerts + MITRE strip */}
            <div className="lg:col-span-5">
              {/* Alerts feed */}
              <div>
                <div className="px-5 py-3 border-b border-blue-400/10 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">incident feed</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">last 60 min</div>
                </div>
                <ul className="divide-y divide-blue-400/5">
                  {ALERTS.map((a, i) => {
                    const c = sevColor[a.sev];
                    return (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 8 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                        className="px-5 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-md border whitespace-nowrap"
                            style={{ color: c.dot, background: c.bg, borderColor: c.border }}
                          >
                            {a.sev}
                          </span>
                          <span className="text-sm text-slate-200 truncate">{a.text}</span>
                        </div>
                        <span className="font-mono text-[10px] text-slate-500 whitespace-nowrap">{a.time}</span>
                      </motion.li>
                    );
                  })}
                </ul>
              </div>

              {/* MITRE coverage strip */}
              <div className="border-t border-blue-400/10">
                <div className="px-5 py-3 border-b border-blue-400/10 flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">ATT&CK coverage</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">12 tactics</div>
                </div>
                <div className="px-5 py-5 grid grid-cols-6 gap-1.5">
                  {TACTICS.map(([name, score], i) => (
                    <div key={name} className="group">
                      <div className="aspect-square rounded-[3px] relative overflow-hidden" style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(96,165,250,0.10)",
                      }}>
                        <div className="absolute inset-0" style={{
                          background: `linear-gradient(180deg, rgba(96,165,250,${0.05 + score * 0.55}) 0%, rgba(6,182,212,${score * 0.35}) 100%)`,
                        }} />
                        <span className="absolute bottom-0.5 right-0.5 font-mono text-[8px] text-white/80">{Math.round(score * 100)}</span>
                      </div>
                      <div className="mt-1 font-mono text-[8px] uppercase tracking-wider text-slate-500 truncate text-center">{name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom metrics strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-blue-400/15">
            {[
              { k: "active.hunts", v: "12", c: "#60A5FA" },
              { k: "auto.responses", v: "247", c: "#22D3EE" },
              { k: "mttr.avg", v: "18m", c: "#818CF8" },
              { k: "coverage", v: "99.4%", c: "#60A5FA" },
            ].map((m, i) => (
              <div
                key={m.k}
                className={`p-5 ${i > 0 ? "border-l border-blue-400/10" : ""}`}
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-60" style={{ color: m.c }}>{m.k}</div>
                <div className="mt-1 font-display font-bold text-2xl md:text-3xl tracking-tighter text-white">{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Labs cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LABS.map((l, i) => {
            const a = accentMap[l.accent];
            const Icon = l.icon;
            return (
              <motion.article
                key={l.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`lab-card-${l.code.toLowerCase()}`}
                className="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col"
                style={{
                  background: "linear-gradient(180deg,rgba(10,12,19,0.85) 0%,rgba(7,10,18,0.95) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div aria-hidden className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                  background: `radial-gradient(340px 200px at 80% 0%, ${a.glow}, transparent 70%)`,
                }} />

                <div className="relative flex items-start justify-between">
                  <div
                    className="h-11 w-11 rounded-lg grid place-items-center transition-transform group-hover:scale-105"
                    style={{ background: a.soft, border: `1px solid ${a.border}`, color: a.dot }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.22em]"
                      style={{ color: a.dot, opacity: 0.7 }}
                    >
                      {l.code}
                    </span>
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border"
                      style={{ color: a.dot, background: a.soft, borderColor: a.border }}
                    >
                      {l.ttp}
                    </span>
                  </div>
                </div>

                <h3 className="relative mt-6 font-display font-semibold text-lg md:text-[1.15rem] tracking-tight text-white leading-snug min-h-[3.25rem]">
                  {l.title}
                </h3>
                <p className="relative mt-3 text-[13px] text-slate-400 leading-relaxed flex-1">
                  {l.desc}
                </p>

                <a
                  href="#contact"
                  className="relative mt-6 pt-4 inline-flex items-center justify-between text-xs font-medium"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <span style={{ color: a.dot }}>Run this in your tenant</span>
                  <ArrowUpRight
                    size={13}
                    style={{ color: a.dot }}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
